import React, { useState, useMemo } from 'react'
import DeckGL from '@deck.gl/react/typed'
import { ScatterplotLayer, BitmapLayer } from '@deck.gl/layers/typed'
import { TileLayer } from '@deck.gl/geo-layers/typed'
import { CoffeeShop } from '../../hooks/useCoffeeShops'
import { logger } from '../../utils/logger'
import { createCoffeeHeatmapLayer } from './layers/CoffeeHeatmapLayer'


import { ViewState, LayerConfig, VisualizationType } from '../../types/map'

// Define PickingInfo type locally to match Deck.gl's expected structure
interface PickingInfo<T = any> {
  index?: number
  picked?: boolean
  object?: T
  x?: number
  y?: number
  coordinate?: [number, number]
  pixel?: [number, number]
  color?: [number, number, number, number]
  layer?: any
}

interface MapViewProps {
  viewState: ViewState
  setViewState: (v: ViewState | ((prev: ViewState) => ViewState)) => void
  coffeeShops: CoffeeShop[]
  layers: LayerConfig[]
  visualizationType?: VisualizationType
  heatmapIntensity?: number
  onDragStateChange?: (isDragging: boolean) => void
}

const OSM_TILE_URL = 'https://tile.openstreetmap.org/{z}/{x}/{y}.png'



const MapView: React.FC<MapViewProps> = ({ viewState, setViewState, coffeeShops, layers, visualizationType = 'scatter', heatmapIntensity = 2, onDragStateChange }) => {
  const [hoveredObject, setHoveredObject] = useState<PickingInfo<CoffeeShop> | null>(null)
  const [pointer, setPointer] = useState<{x: number, y: number} | null>(null)
  const [, setIsDragging] = useState(false)

  // Keep stable data reference to avoid unnecessary layer updates (deck.gl performance best practice)
  const stableCoffeeShops = useMemo(() => coffeeShops, [coffeeShops])

  // TileLayer for map background
  const tileLayer = new TileLayer({
    id: 'osm-tiles',
    data: OSM_TILE_URL,
    minZoom: 0,
    maxZoom: 19,
    tileSize: 256,
    renderSubLayers: (props: any) => {
      const {
        bbox: { west, south, east, north }
      } = props.tile;
      return new BitmapLayer({
        ...props,
        data: null,
        image: props.data,
        bounds: [west, south, east, north]
      });
    }
  })

  // Create both layers once and use visibility to switch (deck.gl best practice)
  const zoomFactor = Math.max(0.5, Math.min(2, viewState.zoom / 12))
  const baseRadius = 80

  // Determine which layers to show based on enabled state
  const showCoffee = layers.find(l => l.name === 'Coffee Shops')?.enabled

  const zoomLevel = Math.floor(viewState.zoom)

  // Heatmap layer - always created but visibility controlled by both layer toggle AND visualization type
  const heatmapLayer = useMemo(() => createCoffeeHeatmapLayer({
    // CRITICAL: Use stable data reference (deck.gl performance best practice)
    data: stableCoffeeShops,
    visible: Boolean(showCoffee && visualizationType === 'heatmap'),
    // Use constants where possible for better performance
    radiusPixels: Math.max(60, baseRadius * zoomFactor),
    intensity: heatmapIntensity,
    threshold: 0.005,
    isPrewarming: false
  }), [stableCoffeeShops, showCoffee, visualizationType, heatmapIntensity, zoomLevel])

  // Scatter plot layer - always created but visibility controlled by both layer toggle AND visualization type
  const scatterLayer = useMemo(() => new ScatterplotLayer({
    id: 'coffee-shops-scatter',
    data: stableCoffeeShops,
    visible: Boolean(showCoffee && visualizationType === 'scatter'),
    getPosition: (d: any) => [d.lon, d.lat],
    getRadius: 250,
    radiusMinPixels: 8,
    radiusMaxPixels: 30,
    getFillColor: (d: any) => hoveredObject && hoveredObject.object && hoveredObject.object.id === d.id ? [255, 255, 255, 255] : [241, 114, 32, 200],
    getLineColor: [0, 0, 0, 200],
    lineWidthMinPixels: 2,
    pickable: true,
    onClick: (info: any) => {
      if (info.object) {
        logger.debug('Clicked coffee shop:', info.object)
      }
    },
    onHover: (info: any) => {
      setHoveredObject(info)
      setPointer(info.x !== undefined && info.y !== undefined ? {x: info.x, y: info.y} : null)
    },
    updateTriggers: {
      getFillColor: [hoveredObject?.object?.id]
    },
  }), [stableCoffeeShops, showCoffee, visualizationType, hoveredObject?.object?.id])

  // Both layers are now created above with proper memoization

  // Placeholder Weather Layer (just a semi-transparent blue rectangle over the map)
  const showWeather = layers.find(l => l.name === 'Weather')?.enabled
  const weatherLayer = new BitmapLayer({
    id: 'weather-placeholder',
    image: null,
    bounds: [-75.3, 39.8, -75.0, 40.1],
    opacity: 0.2,
    visible: Boolean(showWeather),
    parameters: {
      blendColor: [0, 120, 255, 80]
    }
  })

  // Show a warning if very few results (likely API limit or error)
  const showWarning = coffeeShops.length < 5

  // CRITICAL FIX: Always include all layers but control visibility through layer properties
  // This prevents deck.gl layer lifecycle issues when toggling layers on/off
  const deckLayers: any[] = [
    tileLayer,
    weatherLayer,
    heatmapLayer,
    scatterLayer
  ]

  return (
    <>
      <DeckGL
        viewState={viewState}
        onViewStateChange={(e: any) => setViewState(e.viewState)}
        onInteractionStateChange={(state: any) => {
          // Track dragging state and notify parent
          const dragging = state.isDragging || false
          setIsDragging(dragging)
          onDragStateChange?.(dragging)
        }}
        controller={true}
        layers={deckLayers}
        style={{ position: 'relative' }}
      />
      {/* Tooltip for hover - only show for scatter plot visualization */}
      {hoveredObject && hoveredObject.object && pointer && showCoffee && visualizationType === 'scatter' && (
        <div
          style={{
            position: 'absolute',
            left: pointer.x + 10,
            top: pointer.y + 10,
            background: 'rgba(255,255,255,0.95)',
            color: '#222',
            padding: '6px 12px',
            borderRadius: 8,
            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
            pointerEvents: 'none',
            zIndex: 1000,
            fontSize: 14,
            fontWeight: 500
          }}
        >
          <div>{hoveredObject.object?.name}</div>
          {hoveredObject.object?.properties?.['addr:street'] && (
            <div style={{ fontSize: 12, color: '#666' }}>{hoveredObject.object.properties['addr:street']}</div>
          )}
        </div>
      )}
      {/* Warning for few results */}
      {showWarning && showCoffee && (
        <div
          style={{
            position: 'absolute',
            left: 20,
            bottom: 20,
            background: 'rgba(255, 255, 200, 0.98)',
            color: '#b45309',
            padding: '10px 18px',
            borderRadius: 10,
            boxShadow: '0 2px 8px rgba(0,0,0,0.10)',
            zIndex: 1001,
            fontSize: 15,
            fontWeight: 500,
            border: '1px solid #fbbf24'
          }}
        >
          Not seeing many coffee shops? Try zooming in/out or panning the map.<br/>
          (Or the OpenStreetMap API may be rate-limited. Wait a few seconds and try again.)
        </div>
      )}


    </>
  )
}

export default MapView 